import { add } from "date-fns";
import { DEFAULT_PAGE_SIZE_OPTION } from "helpers/PaginationHelper";
import { isFarmer, isMinistryAdviser } from "modules/auth";
import {
    EducationsRoutes,
    HeaderFilter,
    PanelMenu,
    PanelMenuItem,
    RangeDatePicker,
    useDateRangeClick,
} from "modules/common";
import {
    useGetEducation,
    useGetEducations,
    useGetUserEducationsWithReminders,
} from "modules/common/hooks/dippHooks";
import {
    DateRangeFilter,
    EducationDetails,
    EducationFilters,
    EducationsTable,
} from "modules/education";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { ReactSearchAutocomplete } from "react-search-autocomplete";
import { AppRoutes, RouteParams } from "routing";
import { Education, EducationStatusId } from "services";
import { RootState } from "store";
import { getOperationTypes } from "../modules/education/store";

export enum TabLabel {
    Available = "Dostupno",
    Reminders = "Podsjetnici",
    Listened = "Odslušano",
    Applied = "Prijavljeno",
    AvailableGeneral = "Dostupne edukacije",
}

const menuItems: PanelMenuItem[] = [
    {
        label: TabLabel.Available,
        link: EducationsRoutes.Available,
    },
    {
        label: TabLabel.Applied,
        link: EducationsRoutes.Applied,
    },
    {
        label: TabLabel.Listened,
        link: EducationsRoutes.Listened,
    },
    {
        label: TabLabel.Reminders,
        link: EducationsRoutes.Reminders,
    },
];

export const Educations: React.FC = () => {
    const dispatch = useDispatch();
    const { search } = useLocation();
    const history = useHistory();

    const { user } = useSelector((state: RootState) => state.signIn);
    const { profile } = useSelector((state: RootState) => state.profile);

    const roleType = user?.role.value;

    const tabFilters = isFarmer(roleType)
        ? [TabLabel.Available, TabLabel.Applied, TabLabel.Listened, TabLabel.Reminders]
        : isMinistryAdviser(roleType)
        ? [TabLabel.Applied, TabLabel.Reminders]
        : [TabLabel.AvailableGeneral];
    const operationTypes = useSelector((state: RootState) => state.operationTypes.items);

    const formattedOperationTypes = useMemo(() => {
        return operationTypes.map((type, index) => ({ id: index, name: type }));
    }, [operationTypes]);

    useEffect(() => {
        dispatch(getOperationTypes());
    }, [operationTypes, dispatch]);

    const [menuItem, setMenuItem] = useState(menuItems?.find(item => !item.isDisabled));
    const [selectedEducation, setSelectedEducation] = useState<Education | null>(null);
    const [educationId, setEducationId] = useState<string | null>(null);
    const [status, setStatus] = useState(EducationStatusId.Available);
    const [isLoadingFilters, setIsLoadingFilters] = useState(true);

    const [countyFilter, setCountyFilter] = useState<number[]>([]);
    const [expertAreasFilter, setExpertAreasFilter] = useState<string[]>([]);
    const [isMandatoryFilter, setIsMandatoryFilter] = useState(false);
    const [isOupFilter, setIsOupFilter] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [date, setDate] = useState<DateRangeFilter>({
        from: new Date(),
        to: add(new Date(), { months: 3 }),
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE_OPTION);
    const tabFilter = useMemo(() => menuItems.indexOf(menuItem ?? menuItems[0]), [menuItem]);

    useEffect(() => {
        if (!selectedEducation) return;
        const newStatus = selectedEducation?.educationStatuses[0]?.statusId;
        setStatus(newStatus);
        if (educationId) setMenuItem(menuItems[newStatus]);
    }, [educationId, selectedEducation]);

    useEffect(() => {
        const params = new URLSearchParams(search);
        const educationId = params.get(RouteParams.EducationId);
        if (!educationId) setSelectedEducation(null);
        setEducationId(educationId);
    }, [search]);

    useEffect(() => {
        setCurrentPage(1);
    }, [tabFilter]);

    const handleSearch = (item: string) => {
        setSearchTerm(item);
        setCurrentPage(1);
    };

    const handleDayClick = useDateRangeClick({
        date,
        setDate,
        setCurrentPage,
        emptyValue: null,
    });

    const updateRange = (from: Date | undefined, to: Date | undefined) => {
        setDate({
            from: from ?? null,
            to: to ?? null,
        });
        setCurrentPage(1);
    };

    const { data: educations, isFetching: isLoadingEducations } = useGetEducations(
        {
            pageNumber: currentPage,
            pageSize: parseInt(pageSize.value),
            title: searchTerm,
            counties: countyFilter,
            expertAreas: expertAreasFilter,
            dateFrom: date.from,
            dateTo: date.to,
            status: tabFilter,
            isMandatory: isMandatoryFilter,
            isOup: isOupFilter,
            mibpg: profile?.mibpg,
        },
        menuItem?.label !== TabLabel.Reminders && !isLoadingFilters && Boolean(date.from && date.to)
    );

    const { data: educationsWithReminders, isFetching: isLoadingEducationsWithRemainders } =
        useGetUserEducationsWithReminders(Boolean(menuItem?.label === TabLabel.Reminders));

    const { data: educationDetails, isFetching: isLoadingEducation } = useGetEducation(
        educationId! ?? selectedEducation?.uid!,
        Boolean(educationId) || Boolean(selectedEducation?.uid)
    );

    useEffect(() => {
        educationDetails && setSelectedEducation(educationDetails);
    }, [educationDetails]);

    return (
        <div className="educations-container">
            {selectedEducation && (
                <HeaderFilter
                    hasBackButton
                    onBackButtonClicked={() => {
                        if (educationId) history.push(AppRoutes.Education);
                        setSelectedEducation(null);
                    }}
                >
                    <div className="header-filter-title">PODACI O EDUKACIJI</div>
                </HeaderFilter>
            )}

            <div className="educations-layout">
                <div className="educations-left">
                    <PanelMenu
                        items={menuItems}
                        selectedItem={menuItem}
                        onItemClicked={selectedMenuItem => {
                            if (menuItem?.link === selectedMenuItem.link) return;
                            if (educationId) history.push(AppRoutes.Education);
                            setSelectedEducation(null);
                            setMenuItem(selectedMenuItem);
                        }}
                        customInput={
                            tabFilters[tabFilter] !== TabLabel.Reminders &&
                            !selectedEducation && (
                                <ReactSearchAutocomplete
                                    items={formattedOperationTypes}
                                    onSearch={handleSearch}
                                    onSelect={i => handleSearch(i.name)}
                                    placeholder="Pretraži"
                                    className="autocomplete"
                                    showNoResults={false}
                                />
                            )
                        }
                    />
                </div>

                <div className="educations-right">
                    {!selectedEducation && tabFilters[tabFilter] !== TabLabel.Reminders && (
                        <div className="educations-container-header">
                            <HeaderFilter name="educations" title="TEČAJEVI">
                                <EducationFilters
                                    setIsLoading={setIsLoadingFilters}
                                    countyFilter={countyFilter}
                                    setCountyFilter={setCountyFilter}
                                    expertAreasFilter={expertAreasFilter}
                                    setExpertAreasFilter={setExpertAreasFilter}
                                    isMandatoryFilter={isMandatoryFilter}
                                    setIsMandatoryFilter={setIsMandatoryFilter}
                                    isOupFilter={isOupFilter}
                                    setIsOupFilter={setIsOupFilter}
                                    setCurrentPage={setCurrentPage}
                                    setDate={setDate}
                                    date={date}
                                />
                            </HeaderFilter>

                            <div className="educations-date">
                                <RangeDatePicker
                                    onClick={handleDayClick}
                                    selectedDays={{
                                        from: date.from ?? undefined,
                                        to: date.to ?? undefined,
                                    }}
                                    modifiers={{
                                        start: date.from ?? undefined,
                                        end: date.to ?? undefined,
                                    }}
                                    updateRange={updateRange}
                                />
                            </div>
                        </div>
                    )}

                    {selectedEducation ? (
                        <EducationDetails education={selectedEducation} status={status} />
                    ) : (
                        <EducationsTable
                            isLoading={
                                isLoadingEducation ||
                                isLoadingEducations ||
                                isLoadingEducationsWithRemainders
                            }
                            educations={
                                menuItem?.label === TabLabel.Reminders
                                    ? educationsWithReminders
                                    : educations
                            }
                            setSelectedEducation={setSelectedEducation}
                            currentTab={menuItem?.label as TabLabel}
                            roleType={roleType}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
